import React from "react";
import { useTranslation } from "react-i18next";

import {
    Card,
    CardHeader,
    CardHeaderTitle,
    FilterRow,
    FilterLabel,
    SmallToggle,
    ColorSwatch,
    ColorValue,
    ResetIconButton,
} from "./styled";
import { ResetIcon } from "./ResetIcon";
import { OriginKey, OriginConfig } from "./types";
import { ColorPickerPopover } from "../shared/ColorPickerPopover";

type SourcesCardProps = {
    origins: Record<OriginKey, OriginConfig>;
    mainColor: string;
    activeColorPicker: string | null;
    setActiveColorPicker: (key: string | null) => void;
    onUpdateOrigin: (key: OriginKey, updates: Partial<OriginConfig>) => void;
    onReset: () => void;
};

export function SourcesCard({
    origins,
    mainColor,
    activeColorPicker,
    setActiveColorPicker,
    onUpdateOrigin,
    onReset,
}: SourcesCardProps): JSX.Element {
    const { t } = useTranslation();

    const ORIGIN_LABELS: Record<OriginKey, string> = {
        all: t("All"),
        client: t("Client"),
        cuisine: t("Kitchen"),
        pos: t("POS"),
        paiement: t("Payment"),
    };

    return (
        <Card>
            <CardHeader>
                <CardHeaderTitle>{t("Notification sources")}</CardHeaderTitle>
                <ResetIconButton
                    data-testid="servio-notifications-reset-btn"
                    mainColor={mainColor}
                    onClick={onReset}
                    title={t("Reset to default")}
                >
                    <ResetIcon />
                    {t("Reset")}
                </ResetIconButton>
            </CardHeader>
            {(Object.keys(ORIGIN_LABELS) as OriginKey[]).map((key) => (
                <FilterRow
                    key={key}
                    data-testid={`servio-notifications-origin-${key}`}
                >
                    <SmallToggle
                        data-testid={`servio-notifications-origin-${key}-toggle`}
                        isActive={origins[key].enabled}
                        mainColor={mainColor}
                        onClick={() =>
                            onUpdateOrigin(key, {
                                enabled: !origins[key].enabled,
                            })
                        }
                    />
                    <FilterLabel>{ORIGIN_LABELS[key]}</FilterLabel>
                    <ColorSwatch
                        data-testid={`servio-notifications-origin-${key}-color`}
                        color={origins[key].color}
                        onClick={(e) => {
                            // Safari does not auto-focus buttons on click;
                            // forcing it ensures useDialogA11y's previousFocus
                            // captures the swatch so focus is restored here
                            // when the picker closes.
                            e.currentTarget.focus();
                            setActiveColorPicker(
                                activeColorPicker === key ? null : key
                            );
                        }}
                    />
                    <ColorValue>{origins[key].color}</ColorValue>
                    {activeColorPicker === key && (
                        <ColorPickerPopover
                            color={origins[key].color}
                            onChange={(hex) =>
                                onUpdateOrigin(key, { color: hex })
                            }
                            onClose={() => setActiveColorPicker(null)}
                            data-testid={`servio-notifications-origin-${key}-color-picker`}
                        />
                    )}
                </FilterRow>
            ))}
        </Card>
    );
}
